import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { useMe } from '../hooks/useMe'
import { acceptApplication, acceptInviteJob, acceptLocalJob, acceptProposal, addDisputeEvidence, applyToJob, approveJob, cancelJob, claimLocalJob, completeJob, createJobReview, declineInviteJob, declineLocalDelivery, declineLocalJob, deliverJob, disputeJob, fundEscrow, getJob, getJobThread, listApplications, listProposals, openDispute, rebroadcastJob, refundEscrow, releaseEscrow, submitProposal, withdrawProposal } from '../api/jobs'

const PIXE_ATOMIC_SCALE = 1_000_000_000_000n

function pixeFromCents(units?: number | null) {
  if (units == null || !Number.isFinite(units)) return null
  return (units / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

function pixeAtomicToDisplay(amount?: number | null) {
  const atomic = BigInt(amount || 0)
  const whole = atomic / PIXE_ATOMIC_SCALE
  const frac = atomic % PIXE_ATOMIC_SCALE
  if (frac === 0n) return whole.toString()
  const fracStr = frac.toString().padStart(12, '0').replace(/0+$/, '')
  return `${whole.toString()}.${fracStr}`
}

function pixeDisplayToAtomic(input: string): number | null {
  const text = input.trim()
  if (!text) return null
  const m = text.match(/^(\d+)(?:\.(\d{1,12}))?$/)
  if (!m) return null
  const whole = BigInt(m[1])
  const fracRaw = m[2] || ''
  const frac = BigInt((fracRaw + '0'.repeat(12)).slice(0, 12))
  const atomic = whole * PIXE_ATOMIC_SCALE + frac
  if (atomic > BigInt(Number.MAX_SAFE_INTEGER)) return null
  return Number(atomic)
}

function extractErrorMessage(err: any, fallback: string) {
  const data = err?.response?.data
  const availableToSpend = Number(data?.available_to_spend)
  const pending = Number(data?.pending)
  if (Number.isFinite(availableToSpend) || Number.isFinite(pending)) {
    const spendText = Number.isFinite(availableToSpend) ? pixeAtomicToDisplay(availableToSpend) : '0'
    const pendingText = Number.isFinite(pending) ? pixeAtomicToDisplay(pending) : '0'
    return `Funding blocked. Available to spend: ${spendText} PIXE. Pending: ${pendingText} PIXE.`
  }
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail
  if (typeof data?.code === 'string') {
    if (data.code === 'NO_PAYOUT_TARGET') return 'Select or accept a pro before releasing escrow.'
    if (data.code === 'INVITE_NOT_ACCEPTED') return 'The invited pro must accept before escrow can be funded.'
    if (data.code === 'INSUFFICIENT_AVAILABLE') return 'Insufficient available balance for escrow funding.'
    if (data.code === 'MUST_DISPUTE') return 'To cancel after work starts, open a dispute.'
    if (data.code === 'DISPUTE_OPEN') return 'Dispute open. Escrow is locked until resolution.'
    if (data.code === 'DISPUTE_NOT_OPEN') return 'Dispute is not open.'
    return data.code
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message
  return fallback
}

function compensationSummary(job: any): string {
  const mode = String(job?.compensation_mode || '').toUpperCase()
  const proposedHourly = Number(job?.proposed_hourly_pixe)
  const min = pixeFromCents(job?.min_rate_cents)
  const max = pixeFromCents(job?.max_rate_cents)
  const fixed = pixeFromCents(job?.fixed_price_cents)

  if (mode === 'FIXED') {
    return fixed ? `Fixed: ${fixed} PIXE` : 'Fixed'
  }
  if (mode === 'HOURLY') {
    if (Number.isFinite(proposedHourly) && proposedHourly > 0) return `Hourly: ${proposedHourly} PIXE/hr`
    if (min && max) return min === max ? `Hourly: ${min} PIXE/hr` : `Hourly: ${min}-${max} PIXE/hr`
    if (min) return `Hourly: ${min} PIXE/hr`
    if (max) return `Hourly: ${max} PIXE/hr`
    return 'Hourly'
  }
  if (mode === 'SALARY') {
    const salary = pixeFromCents(job?.salary_cents)
    return salary ? `Salary: ${salary} PIXE` : 'Salary'
  }
  return mode || 'Compensation not set'
}

export default function JobDetail() {
  const { id } = useParams()
  const authed = Boolean(getToken())
  const { me } = useMe(authed)

  const [job, setJob] = useState<any | null>(null)
  const [apps, setApps] = useState<any[]>([])
  const [coverNote, setCoverNote] = useState('')
  const [proposedRate, setProposedRate] = useState('')
  const [escrowAmountInput, setEscrowAmountInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [escrowBusy, setEscrowBusy] = useState(false)
  const [disputeBusy, setDisputeBusy] = useState(false)
  const [canViewApplications, setCanViewApplications] = useState(false)
  const [disputeMessage, setDisputeMessage] = useState('')
  const [disputeAttachmentsInput, setDisputeAttachmentsInput] = useState('')
  const [jobThreadId, setJobThreadId] = useState<number | null>(null)
  const [proposalPrice, setProposalPrice] = useState('')
  const [proposalHourly, setProposalHourly] = useState('')
  const [proposalTimeline, setProposalTimeline] = useState('')
  const [proposalMessage, setProposalMessage] = useState('')
  const [proposalBusy, setProposalBusy] = useState(false)
  const [proposals, setProposals] = useState<any[]>([])
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewBody, setReviewBody] = useState('')
  const [reviewBusy, setReviewBusy] = useState(false)

  const jobId = useMemo(() => Number(id), [id])
  const isOwner = Boolean(job && me?.id && Number(job.created_by) === Number(me.id))
  const isTargetPro = Boolean(job && me?.id && Number(job.target_pro_user_id || job.target_pro_user || 0) === Number(me.id))
  const isContract = String(job?.job_kind || '') === 'CONTRACT'
  const isLocal = String(job?.job_mode || 'REMOTE') === 'LOCAL'
  const escrowStatus = String(job?.escrow_status || 'NONE')
  const jobStatus = String(job?.status || '')
  const payoutTargetId = Number(job?.accepted_applicant_user_id || job?.target_pro_user_id || job?.target_pro_user || 0)
  const isPayoutTarget = Boolean(job && me?.id && payoutTargetId && Number(payoutTargetId) === Number(me.id))
  const viewerDeliveryStatus = String(job?.viewer_delivery_status || '')
  const isLocalOpen = Boolean(isLocal && String(job?.job_visibility || '') === 'OPEN')
  const canClaimLocalOpen = Boolean(isLocalOpen && viewerDeliveryStatus === 'PENDING')
  const pendingDeliveriesCount = Number(job?.pending_deliveries_count || 0)
  const rebroadcastCount = Number(job?.rebroadcast_count || 0)
  const canRebroadcast = Boolean(job?.can_rebroadcast)
  const isDisputed = jobStatus === 'DISPUTED'
  const canOpenDispute = Boolean(isContract && escrowStatus === 'HELD' && !isDisputed && (isOwner || isPayoutTarget))
  const invitationStatusRaw = job?.invitation_status
  const isInviteJob = invitationStatusRaw !== null && invitationStatusRaw !== undefined && String(invitationStatusRaw) !== ''
  const invitationStatus = isInviteJob ? String(invitationStatusRaw) : ''
  const isInvitePending = Boolean(isContract && String(job?.job_visibility || '') === 'DIRECT' && invitationStatus === 'PENDING')
  const isInviteDeclined = Boolean(isInviteJob && invitationStatus === 'DECLINED')
  const escrowLockedByInvite = Boolean(isInvitePending || isInviteDeclined)
  const canMarkComplete = Boolean(
    isContract &&
      !job?.completed_at &&
      (isOwner || isPayoutTarget) &&
      !isInvitePending &&
      !isInviteDeclined &&
      (!isInviteJob || escrowStatus === 'HELD'),
  )
  const canMarkDelivered = Boolean(
    isContract &&
      isPayoutTarget &&
      escrowStatus === 'HELD' &&
      [ 'OPEN', 'FILLED', 'IN_PROGRESS' ].includes(jobStatus),
  )
  const canApproveDelivery = Boolean(isContract && isOwner && escrowStatus === 'HELD' && jobStatus === 'DELIVERED')
  const canClientDisputeDelivery = Boolean(isContract && isOwner && escrowStatus === 'HELD' && jobStatus === 'DELIVERED')
  const canLeaveReview = Boolean(isOwner && jobStatus === 'COMPLETED')
  const canPropose = Boolean(
    authed &&
      !isOwner &&
      isContract &&
      String(job?.job_visibility || '') === 'OPEN' &&
      String(job?.status || '') === 'OPEN' &&
      (String(job?.job_mode || 'REMOTE') !== 'LOCAL' || String(job?.urgency || 'FLEXIBLE') === 'FLEXIBLE'),
  )

  async function refreshJob(nextId?: number) {
    const targetId = nextId || job?.id || jobId
    if (!Number.isFinite(targetId) || targetId <= 0) return
    const data = await getJob(targetId)
    setJob(data)
  }

  async function refreshApplications(nextJob?: any) {
    const source = nextJob || job
    if (!source) return
    if (String(source?.job_visibility || '') === 'DIRECT') {
      setApps([])
      setCanViewApplications(false)
      return
    }
    try {
      const data = await listApplications(source.id)
      setApps(Array.isArray(data?.results) ? data.results : [])
      setCanViewApplications(true)
    } catch {
      setApps([])
      setCanViewApplications(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(jobId) || jobId <= 0) return
    ;(async () => {
      try {
        setError(null)
        const data = await getJob(jobId)
        setJob(data)
        await refreshApplications(data)
      } catch (e: any) {
        setError(extractErrorMessage(e, 'Failed to load job'))
      }
    })()
  }, [jobId])

  useEffect(() => {
    if (!job || !isOwner) {
      setProposals([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await listProposals(job.id)
        if (!cancelled) setProposals(Array.isArray(data?.results) ? data.results : [])
      } catch {
        if (!cancelled) setProposals([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [job?.id, isOwner])

  useEffect(() => {
    if (!job || !authed) {
      setJobThreadId(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await getJobThread(job.id)
        if (!cancelled) setJobThreadId(Number(data?.thread_id || 0) || null)
      } catch {
        if (!cancelled) setJobThreadId(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [job?.id, authed, me?.id])

  useEffect(() => {
    if (!job) return
    if (job.escrow_amount_pixe && !escrowAmountInput.trim()) {
      setEscrowAmountInput(pixeAtomicToDisplay(job.escrow_amount_pixe))
    }
  }, [job, escrowAmountInput])

  async function onApply() {
    if (!job) return
    setSaving(true)
    setError(null)
    try {
      await applyToJob(job.id, {
        cover_note: coverNote,
        proposed_rate_cents: proposedRate.trim() ? Number(proposedRate) : null,
      })
      setCoverNote('')
      setProposedRate('')
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Apply failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onAccept(appId: number) {
    if (!job) return
    try {
      setError(null)
      await acceptApplication(job.id, appId)
      await refreshApplications()
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Accept failed'))
    }
  }

  async function onFundEscrow() {
    if (!job) return
    const amountAtomic = pixeDisplayToAtomic(escrowAmountInput)
    if (!amountAtomic || amountAtomic <= 0) {
      setError('Enter a valid escrow amount in PIXE.')
      return
    }
    try {
      setEscrowBusy(true)
      setError(null)
      await fundEscrow(job.id, amountAtomic)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Funding escrow failed'))
    } finally {
      setEscrowBusy(false)
    }
  }

  async function onReleaseEscrow() {
    if (!job) return
    if (!window.confirm('Release escrow to the selected pro? This cannot be undone.')) return
    try {
      setEscrowBusy(true)
      setError(null)
      await releaseEscrow(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Escrow release failed'))
    } finally {
      setEscrowBusy(false)
    }
  }

  async function onRefundEscrow() {
    if (!job) return
    if (!window.confirm('Refund escrow back to your wallet?')) return
    try {
      setEscrowBusy(true)
      setError(null)
      await refundEscrow(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Escrow refund failed'))
    } finally {
      setEscrowBusy(false)
    }
  }

  async function onCompleteJob() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      await completeJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Mark complete failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onMarkDelivered() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      const note = (window.prompt('Optional delivery note') || '').trim()
      await deliverJob(job.id, note ? { note } : {})
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Mark delivered failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onApproveDelivery() {
    if (!job) return
    if (!window.confirm('Approve delivery and release escrow?')) return
    try {
      setEscrowBusy(true)
      setError(null)
      await approveJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Approval failed'))
    } finally {
      setEscrowBusy(false)
    }
  }

  async function onClientDispute() {
    if (!job) return
    const reason = (window.prompt('Dispute reason') || '').trim()
    if (!reason) return
    const details = (window.prompt('Optional details') || '').trim()
    try {
      setDisputeBusy(true)
      setError(null)
      await disputeJob(job.id, { reason, details: details || undefined })
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Open dispute failed'))
    } finally {
      setDisputeBusy(false)
    }
  }

  async function onOpenDispute() {
    if (!job) return
    const message = disputeMessage.trim()
    if (!message) {
      setError('Add a dispute message before opening.')
      return
    }
    const attachments = disputeAttachmentsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    try {
      setDisputeBusy(true)
      setError(null)
      if (isOwner && String(job?.status || '') === 'DELIVERED') {
        await disputeJob(job.id, { reason: message, details: attachments.join(', ') || undefined })
      } else {
        await openDispute(job.id)
        await addDisputeEvidence(job.id, { message, attachments })
      }
      setDisputeMessage('')
      setDisputeAttachmentsInput('')
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Open dispute failed'))
    } finally {
      setDisputeBusy(false)
    }
  }

  async function onSubmitReview() {
    if (!job) return
    try {
      setReviewBusy(true)
      setError(null)
      await createJobReview(job.id, { rating: Math.max(1, Math.min(5, Number(reviewRating || 5))), body: reviewBody.trim() || undefined })
      setReviewBody('')
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Review submit failed'))
    } finally {
      setReviewBusy(false)
    }
  }

  async function onCancelJob() {
    if (!job) return
    const reason = window.prompt('Optional cancellation reason') || undefined
    try {
      setSaving(true)
      setError(null)
      await cancelJob(job.id, reason)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Cancel failed'))
    } finally {
      setSaving(false)
    }
  }

  const canApply = Boolean(job && job.status === 'OPEN' && job.job_visibility === 'OPEN')
  const canRespondLocal = Boolean(isLocal && isTargetPro && String(job?.job_visibility || '') === 'DIRECT' && !job?.accepted_at && !job?.declined_at)

  async function onClaimLocal() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      await claimLocalJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Claim failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onAcceptLocal() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      await acceptLocalJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Accept failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onDeclineLocal(isOpenDelivery = false) {
    if (!job) return
    const reason = window.prompt('Optional decline reason')
    try {
      setSaving(true)
      setError(null)
      if (isOpenDelivery) {
        await declineLocalDelivery(job.id, reason || undefined)
      } else {
        await declineLocalJob(job.id, reason || undefined)
      }
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Decline failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onAcceptInvite() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      await acceptInviteJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Accept invite failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onDeclineInvite() {
    if (!job) return
    if (!window.confirm('Decline this invitation?')) return
    try {
      setSaving(true)
      setError(null)
      await declineInviteJob(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Decline invite failed'))
    } finally {
      setSaving(false)
    }
  }

  async function onRebroadcast() {
    if (!job) return
    try {
      setSaving(true)
      setError(null)
      const res = await rebroadcastJob(job.id)
      await refreshJob()
      window.alert(`Sent to ${Number((res as any)?.created_deliveries_count || 0)} pros.`)
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'REBROADCAST_LIMIT_REACHED') {
        setError("You've reached the maximum rebroadcast attempts for this request.")
      } else {
        setError(extractErrorMessage(e, 'Rebroadcast failed'))
      }
    } finally {
      setSaving(false)
    }
  }

  async function onSubmitProposal() {
    if (!job) return
    const price = proposalPrice.trim() ? Number(proposalPrice) : null
    const hourly = proposalHourly.trim() ? Number(proposalHourly) : null
    const timeline = proposalTimeline.trim() ? Number(proposalTimeline) : null
    if (!price && !hourly) {
      setError('Set either proposed price or hourly amount (PIXE atomic).')
      return
    }
    try {
      setProposalBusy(true)
      setError(null)
      const data = await submitProposal(job.id, {
        proposed_price_pixe: Number.isFinite(price as number) ? price : null,
        proposed_hourly_pixe: Number.isFinite(hourly as number) ? hourly : null,
        proposed_timeline_days: Number.isFinite(timeline as number) ? timeline : null,
        message: proposalMessage,
      })
      if (data?.thread_id) setJobThreadId(Number(data.thread_id))
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Submit proposal failed'))
    } finally {
      setProposalBusy(false)
    }
  }

  async function onWithdrawProposal() {
    if (!job) return
    try {
      setProposalBusy(true)
      setError(null)
      await withdrawProposal(job.id)
      await refreshJob()
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Withdraw failed'))
    } finally {
      setProposalBusy(false)
    }
  }

  async function onAcceptProposal(proposalId: number) {
    if (!job) return
    try {
      setProposalBusy(true)
      setError(null)
      const data = await acceptProposal(job.id, proposalId)
      setJob(data.job)
      if (data.thread_id) setJobThreadId(Number(data.thread_id))
      const list = await listProposals(job.id)
      setProposals(Array.isArray(list?.results) ? list.results : [])
    } catch (e: any) {
      setError(extractErrorMessage(e, 'Accept proposal failed'))
    } finally {
      setProposalBusy(false)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-4xl px-4 py-6 space-y-4">
        <Link to="/" className="text-sm text-white/70 hover:text-white">Back to feed</Link>
        {error ? <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div> : null}
        {!job ? <div className="glass h-48 rounded-xl" /> : (
          <>
            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
              <div className="text-xs text-white/60 uppercase">{job.job_kind} - {job.job_visibility} - {job.status}</div>
              <h1 className="text-3xl font-extrabold">{job.title}</h1>
              {job.summary ? <p className="text-white/85">{job.summary}</p> : null}
              {job.details_md ? <pre className="whitespace-pre-wrap text-sm text-white/80">{job.details_md}</pre> : null}
              <div className="text-sm text-white/75">Location: {job.location_mode}{job.location_text ? ` - ${job.location_text}` : ''}</div>
              {isLocal ? (
                <div className="rounded-lg border border-sky-400/25 bg-sky-500/10 p-3 text-sm text-sky-100 space-y-1">
                  <div className="font-semibold">Local request</div>
                  <div>Location: {job.client_location_text || 'Not provided'} {job.client_zipcode ? `(${job.client_zipcode})` : ''}</div>
                  <div>Urgency: {job.urgency || 'FLEXIBLE'}</div>
                  {job.scheduled_for ? <div>Scheduled: {new Date(job.scheduled_for).toLocaleString()}</div> : null}
                  {Array.isArray(job.attachments) && job.attachments.length ? (
                    <div className="text-xs text-sky-100/90">Attachments: {job.attachments.length}</div>
                  ) : null}
                </div>
              ) : null}
              <div className="text-sm text-white/75">Compensation: {compensationSummary(job)}</div>
              {(job.compensation_mode || '') === 'HOURLY' ? <div className="text-xs text-white/60">1 PIXE = $1 (for now).</div> : null}
              {Array.isArray(job.skills_data) && job.skills_data.length ? (
                <div className="flex flex-wrap gap-2">
                  {job.skills_data.map((s: any) => <span key={s.slug} className="rounded-full border border-white/15 px-2 py-1 text-xs">{s.name}</span>)}
                </div>
              ) : null}
            </section>

            {canRespondLocal ? (
              <section className="rounded-2xl border border-sky-400/25 bg-sky-500/10 p-5 space-y-3">
                <div className="text-sm font-semibold text-sky-100">Respond to local request</div>
                <div className="flex gap-2">
                  <button type="button" onClick={onAcceptLocal} disabled={saving} className="rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-60">
                    {saving ? 'Working...' : 'Accept'}
                  </button>
                  <button type="button" onClick={() => onDeclineLocal()} disabled={saving} className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
                    Decline
                  </button>
                </div>
              </section>
            ) : null}

            {isInvitePending && isTargetPro ? (
              <section className="rounded-2xl border border-indigo-400/25 bg-indigo-500/10 p-5 space-y-3">
                <div className="text-sm font-semibold text-indigo-100">You were invited to this job</div>
                <div className="text-xs text-indigo-100/80">Accept to start work and unlock escrow funding.</div>
                <div className="flex gap-2">
                  <button type="button" onClick={onAcceptInvite} disabled={saving} className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:opacity-60">
                    {saving ? 'Working...' : 'Accept invite'}
                  </button>
                  <button type="button" onClick={onDeclineInvite} disabled={saving} className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
                    Decline
                  </button>
                </div>
              </section>
            ) : null}

            {isInvitePending && isOwner ? (
              <section className="rounded-2xl border border-indigo-400/25 bg-indigo-500/10 p-5 text-sm text-indigo-100">
                Waiting for pro to accept this invitation before escrow can be funded.
              </section>
            ) : null}

            {isInviteDeclined ? (
              <section className="rounded-2xl border border-rose-400/25 bg-rose-500/10 p-5 text-sm text-rose-100">
                Invite declined.
              </section>
            ) : null}

            {canClaimLocalOpen ? (
              <section className="rounded-2xl border border-sky-400/25 bg-sky-500/10 p-5 space-y-3">
                <div className="text-sm font-semibold text-sky-100">This local request is available to claim</div>
                <div className="flex gap-2">
                  <button type="button" onClick={onClaimLocal} disabled={saving} className="rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-60">
                    {saving ? 'Working...' : 'Claim'}
                  </button>
                  <button type="button" onClick={() => onDeclineLocal(true)} disabled={saving} className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
                    Decline
                  </button>
                </div>
              </section>
            ) : null}

            {isLocalOpen && isOwner && !job?.claimed_pro_user_id && pendingDeliveriesCount > 0 ? (
              <section className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-5 text-sm text-amber-100">
                Waiting for a Pro to claim this request (pending: {pendingDeliveriesCount}).
              </section>
            ) : null}

            {isLocalOpen && isOwner && !job?.claimed_pro_user_id && pendingDeliveriesCount === 0 ? (
              <section className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-5 space-y-3">
                <div className="text-sm text-amber-100">No pros are currently available.</div>
                <button
                  type="button"
                  onClick={onRebroadcast}
                  disabled={saving || !canRebroadcast}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                >
                  Rebroadcast request ({Math.max(0, 3 - rebroadcastCount)} left)
                </button>
              </section>
            ) : null}

            {isLocal && job?.claimed_pro_user_id ? (
              <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5 text-sm text-emerald-100">
                {isOwner ? `Claimed by Pro #${job.claimed_pro_user_id}. Fund escrow to lock payment.` : isPayoutTarget ? 'You claimed this job.' : `Claimed by Pro #${job.claimed_pro_user_id}.`}
              </section>
            ) : null}

            {isLocal && job?.accepted_at ? (
              <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5 text-sm text-emerald-100">
                Accepted. Client can fund escrow to lock payment.
              </section>
            ) : null}

            {isContract && escrowStatus === 'HELD' && String(job?.status || '') === 'COMPLETED' && !isDisputed ? (
              <section className="rounded-2xl border border-amber-400/25 bg-amber-500/10 p-5 text-sm text-amber-100">
                Auto-release on {job.auto_release_at ? new Date(job.auto_release_at).toLocaleString() : 'scheduled time'} unless disputed.
              </section>
            ) : null}

            {isDisputed ? (
              <section className="rounded-2xl border border-red-400/25 bg-red-500/10 p-5 text-sm text-red-100">
                Dispute open. Escrow is locked until resolution.
              </section>
            ) : null}

            {canMarkDelivered ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Delivery</div>
                <button
                  type="button"
                  onClick={onMarkDelivered}
                  disabled={saving}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                >
                  {saving ? 'Working...' : 'Mark Delivered'}
                </button>
                <div className="text-xs text-white/65">Client will be prompted to approve and release escrow.</div>
              </section>
            ) : null}

            {canApproveDelivery ? (
              <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5 space-y-3">
                <div className="text-sm font-semibold text-emerald-100">Delivery received</div>
                <div className="text-xs text-emerald-50">Approve to release escrow now, or open a dispute.</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onApproveDelivery}
                    disabled={escrowBusy}
                    className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {escrowBusy ? 'Working...' : 'Approve & Release Escrow'}
                  </button>
                  {canClientDisputeDelivery ? (
                    <button
                      type="button"
                      onClick={onClientDispute}
                      disabled={disputeBusy}
                      className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                    >
                      {disputeBusy ? 'Working...' : 'Open Dispute'}
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {canMarkComplete ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Job completion</div>
                <button
                  type="button"
                  onClick={onCompleteJob}
                  disabled={saving}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                >
                  {saving ? 'Working...' : 'Mark complete'}
                </button>
              </section>
            ) : null}

            {canOpenDispute ? (
              <section className="rounded-2xl border border-red-400/25 bg-red-500/10 p-5 space-y-3">
                <div className="text-sm font-semibold text-red-100">Open dispute</div>
                <textarea
                  value={disputeMessage}
                  onChange={(e) => setDisputeMessage(e.target.value)}
                  rows={3}
                  placeholder="Describe the issue"
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"
                />
                <input
                  value={disputeAttachmentsInput}
                  onChange={(e) => setDisputeAttachmentsInput(e.target.value)}
                  placeholder="Attachment URLs (comma-separated, optional)"
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={onOpenDispute}
                  disabled={disputeBusy}
                  className="rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60"
                >
                  {disputeBusy ? 'Opening...' : 'Open dispute'}
                </button>
              </section>
            ) : null}

            {isOwner ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">{isInvitePending ? 'Cancel invite' : 'Cancel job'}</div>
                <div className="text-xs text-white/65">
                  {isInvitePending ? 'This will close the invitation. No funds have been held.' : 'If escrow is held and work has started, cancellation requires opening a dispute.'}
                </div>
                <button
                  type="button"
                  onClick={onCancelJob}
                  disabled={saving}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                >
                  {saving ? 'Working...' : isInvitePending ? 'Cancel invite' : 'Cancel job'}
                </button>
              </section>
            ) : null}

            {isOwner && isContract && escrowStatus === 'NONE' ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
                <div className="text-sm font-semibold">Fund Escrow</div>
                <div className="text-xs text-white/70">
                  {escrowLockedByInvite ? 'Escrow unlocks after the invited pro accepts.' : 'Enter amount in PIXE.'}
                </div>
                <input
                  value={escrowAmountInput}
                  onChange={(e) => setEscrowAmountInput(e.target.value)}
                  placeholder="0.0"
                  disabled={escrowLockedByInvite}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={onFundEscrow}
                  disabled={escrowBusy || escrowLockedByInvite}
                  className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                >
                  {escrowBusy ? 'Funding...' : 'Fund'}
                </button>
              </section>
            ) : null}

            {isOwner && isContract && escrowStatus === 'HELD' ? (
              <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5 space-y-3">
                <div className="inline-flex rounded-full border border-emerald-400/35 bg-emerald-500/15 px-2 py-1 text-xs text-emerald-100">
                  Escrow Funded
                </div>
                <div className="text-sm text-emerald-50">
                  Total held: {pixeAtomicToDisplay(job.escrow_amount_pixe)} PIXE
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onReleaseEscrow}
                    disabled={escrowBusy}
                    className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
                  >
                    {escrowBusy ? 'Working...' : 'Release Payment'}
                  </button>
                  <button
                    type="button"
                    onClick={onRefundEscrow}
                    disabled={escrowBusy}
                    className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                  >
                    Refund Escrow
                  </button>
                </div>
              </section>
            ) : null}

            {isContract && escrowStatus === 'RELEASED' ? (
              <section className="rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-5 space-y-2">
                <div className="text-sm font-semibold text-emerald-100">Escrow Released</div>
                <div className="text-xs text-emerald-50">Total {pixeAtomicToDisplay(job.escrow_amount_pixe)} PIXE - Fee {pixeAtomicToDisplay(job.escrow_fee_pixe)} PIXE - Net {pixeAtomicToDisplay(job.escrow_net_pixe)} PIXE</div>
              </section>
            ) : null}

            {canLeaveReview ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
                <div className="text-sm font-semibold">Review</div>
                {job?.review ? (
                  <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                    <div className="text-yellow-400">{'★'.repeat(Math.max(1, Math.min(5, Number(job.review.rating || 0))))}</div>
                    <div className="mt-1 text-sm text-white/85">{job.review.body || 'No written feedback.'}</div>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setReviewRating(n)}
                          className={`h-8 w-8 rounded-md border ${reviewRating >= n ? 'border-yellow-300/50 bg-yellow-500/20 text-yellow-300' : 'border-white/15 bg-white/5 text-white/50'}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={reviewBody}
                      onChange={(e) => setReviewBody(e.target.value)}
                      rows={3}
                      placeholder="Optional review"
                      className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={onSubmitReview}
                      disabled={reviewBusy}
                      className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                    >
                      {reviewBusy ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </>
                )}
              </section>
            ) : null}

            {isContract && isPayoutTarget && escrowStatus === 'HELD' ? (
              <section className="rounded-2xl border border-sky-400/30 bg-sky-500/10 p-5 text-sm text-sky-100">
                Escrow funded by client.
              </section>
            ) : null}

            {isContract && isPayoutTarget && escrowStatus === 'RELEASED' ? (
              <section className="rounded-2xl border border-sky-400/30 bg-sky-500/10 p-5 text-sm text-sky-100">
                Payment released: {pixeAtomicToDisplay(job.escrow_net_pixe)} PIXE
              </section>
            ) : null}

            {isDisputed && Array.isArray(job?.dispute_evidence_recent) && job.dispute_evidence_recent.length ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Dispute evidence</div>
                <div className="space-y-2">
                  {job.dispute_evidence_recent.map((item: any) => (
                    <div key={item.id} className="rounded-lg border border-white/10 p-3 text-sm">
                      <div className="text-white/70">User #{item.submitted_by_id}</div>
                      <div className="text-white/90">{item.message}</div>
                      {Array.isArray(item.attachments) && item.attachments.length ? (
                        <div className="text-xs text-white/60">Attachments: {item.attachments.length}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {canApply ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Apply</div>
                <textarea value={coverNote} onChange={(e) => setCoverNote(e.target.value)} rows={4} placeholder="Cover note" className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                <input value={proposedRate} onChange={(e) => setProposedRate(e.target.value)} placeholder="Proposed rate (PIXE/hr x100, optional)" className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                <button type="button" onClick={onApply} disabled={saving} className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
                  {saving ? 'Submitting...' : 'Apply'}
                </button>
              </section>
            ) : null}

            {canPropose ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Submit Proposal</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input value={proposalPrice} onChange={(e) => setProposalPrice(e.target.value)} placeholder="Price (PIXE atomic)" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                  <input value={proposalHourly} onChange={(e) => setProposalHourly(e.target.value)} placeholder="Hourly (PIXE atomic)" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                  <input value={proposalTimeline} onChange={(e) => setProposalTimeline(e.target.value)} placeholder="Timeline (days)" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                </div>
                <textarea value={proposalMessage} onChange={(e) => setProposalMessage(e.target.value)} rows={4} placeholder="Proposal message" className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <button type="button" onClick={onSubmitProposal} disabled={proposalBusy} className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
                    {proposalBusy ? 'Saving...' : 'Submit proposal'}
                  </button>
                  {String(job?.my_proposal?.status || '') === 'SUBMITTED' ? (
                    <button type="button" onClick={onWithdrawProposal} disabled={proposalBusy} className="rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
                      Withdraw
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {isOwner && isContract && String(job?.job_visibility || '') === 'OPEN' ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
                <div className="text-sm font-semibold">Proposals ({proposals.length})</div>
                {!proposals.length ? <div className="text-sm text-white/65">No proposals yet.</div> : (
                  <div className="space-y-2">
                    {proposals.map((p) => (
                      <div key={p.id} className="rounded-lg border border-white/10 p-3 text-sm">
                        <div className="font-semibold text-white">{p.pro_display_name || p.pro_handle || `Pro #${p.pro_user}`}</div>
                        <div className="text-white/70">Rating {Number(p.pro_rating || 0).toFixed(2)} · Completed {Number(p.pro_completed_jobs_count || 0)}</div>
                        <div className="text-white/80">Price: {p.proposed_price_pixe ?? '—'} PIXE · Hourly: {p.proposed_hourly_pixe ?? '—'} PIXE</div>
                        {p.proposed_timeline_days != null ? <div className="text-white/70">Timeline: {p.proposed_timeline_days} days</div> : null}
                        {p.message ? <div className="text-white/85 mt-1">{p.message}</div> : null}
                        <div className="mt-2 text-xs text-white/60">Status: {p.status}</div>
                        {String(p.status) === 'SUBMITTED' ? (
                          <button type="button" onClick={() => onAcceptProposal(p.id)} disabled={proposalBusy} className="mt-2 rounded-lg border border-white/20 bg-white/5 px-3 py-1 text-xs hover:bg-white/10 disabled:opacity-60">
                            Accept proposal
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
              <div className="text-sm font-semibold">Messages</div>
              {jobThreadId ? (
                <Link
                  to={`/inbox/i/${jobThreadId}`}
                  className="inline-flex rounded-xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  Open chat
                </Link>
              ) : (
                <div className="text-sm text-white/65">Messages will appear once a pro is selected.</div>
              )}
            </section>

            {canViewApplications && String(job?.job_visibility || '') !== 'DIRECT' ? (
              <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
                <div className="text-sm font-semibold">Applications</div>
                {!apps.length ? <div className="text-sm text-white/70">No applications yet.</div> : (
                  <div className="space-y-2">
                    {apps.map((app) => (
                      <div key={app.id} className="rounded-lg border border-white/10 p-3 text-sm">
                        <div className="text-white/80">Applicant #{app.applicant_user}</div>
                        <div className="text-white/70">{app.cover_note || 'No cover note'}</div>
                        <div className="text-white/70">Status: {app.status}</div>
                        {app.status !== 'ACCEPTED' ? (
                          <button type="button" onClick={() => onAccept(app.id)} className="mt-2 rounded-lg border border-white/20 bg-white/5 px-3 py-1 text-xs hover:bg-white/10">
                            Accept
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}
